/**
 * AOP 2027 Planning — API client for `/api/aop-planning` (`proxy-server/routes/aop-planning.js`).
 *
 * Its own client, calling only its own routes: the AOP page must not touch the Org Planning
 * Overview's endpoints, so a department lead's draft can never become a regular proposal. Types
 * and error classes are borrowed from the overview's clients — the tree and the form have the same
 * shape — but nothing here calls them.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { RoleCompensation } from '../types/compensation';
import {
  BulkPartialFailureError,
  BulkProposalValidationError,
  InternalPlaygroundAccessError,
  ProposalValidationError,
  type BulkProposalInput,
  type HiringCapacityTimeline,
  type ProposedRoleInput,
} from './internalPlaygroundApi';
import { RoleEditValidationError, type RoleEditValues } from './plannedRoleEditsApi';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const AOP_URL = `${PROXY_URL}/api/aop-planning`;

/**
 * The AOP page plans no CAC (Anna, Oct 27 2026; `proxy-server/lib/aopCac.js`): the proxy leaves CAC
 * people and roles out and refuses a CAC draft, so the forms do not offer the choice. Applied to the
 * form vocabularies the AOP dialogs read.
 */
export function withoutCacChoice<T extends { costCategory: string[] }>(options: T | undefined): T | undefined {
  return options ? { ...options, costCategory: options.costCategory.filter((c) => c !== 'CAC') } : options;
}

/** One cache entry for the AOP page — never shared with the Org Planning Overview's. */
export const AOP_TIMELINE_QUERY_KEY = ['aop-planning-timeline'];

/** The leaf `source` the proxy gives an AOP draft — the only rows this page edits. */
export const AOP_SOURCE = 'aop';

/** One month of a department's targets beside its plan (`proxy-server/lib/aopTargets.js`). */
export interface AopTargetMonth {
  budgetFte: number | null;
  /** The tree's committed FTE (staff + approved roles). Optional: an older proxy revision has none. */
  committedFte?: number;
  /** The department's AOP drafts. Optional, as `committedFte`. */
  draftFte?: number;
  /** Committed FTE + the department's AOP drafts. */
  planFte: number;
  budgetCost: number | null;
  /** Existing staff + committed roles, from Anna's model — one aggregate. */
  baselineCost: number | null;
  /** Null when `costHidden`. */
  draftCost: number | null;
  /** Baseline + drafts; null when either is missing or hidden. */
  planCost: number | null;
}

export interface AopDepartmentTargets {
  months: Record<string, AopTargetMonth>;
  kpis: { kpi: string; unit: string | null; byMonth: Record<string, { target: number | null; perFte: number | null }> }[];
  /** A draft here uses a salary band this viewer may not see, so draft and plan cost are left out. */
  costHidden: boolean;
  /** The salary bands could not be read, so draft and plan cost are blank. */
  bandsUnavailable?: boolean;
  /** Drafts with no price yet (no track and level, start date or band). */
  uncostedDrafts: number;
  /** False until Anna has entered a row for this department. */
  hasTargets: boolean;
}

/** One month of the company totals: like `AopTargetMonth`, but any figure can be blank. */
export interface AopCompanyMonth {
  committedFte: number | null;
  draftFte: number | null;
  planFte: number | null;
  budgetFte: number | null;
  budgetCost: number | null;
  baselineCost: number | null;
  /** Null when any department's draft cost is hidden or unpriced. */
  draftCost: number | null;
  planCost: number | null;
}

/** The departments added up for the Overview tab (`companyTotals` in `proxy-server/lib/aopTargets.js`).
 * Plan and budget cover the same departments; the ones without a budget are named in `leftOut`. */
export interface AopCompanyTargets {
  months: Record<string, AopCompanyMonth>;
  /** Department labels left out of the FTE and of the cost figures, for want of a budget. */
  leftOut: { fte: string[]; cost: string[] };
  costHidden: boolean;
  bandsUnavailable: boolean;
  /** Drafts in the cost figures with no price yet, counted at €0 (as `AopDepartmentTargets`). */
  uncostedDrafts: number;
}

/** What "Sync to hiring plan" did (`proxy-server/lib/aopSync.js`). */
export interface AopSyncResult {
  at: string;
  by: string;
  created: number;
  updated: number;
  cancelled: number;
  skipped: number;
  failed: number;
  rows: { title: string; outcome: 'created' | 'updated' | 'cancelled' | 'skipped' | 'failed'; detail: string | null }[];
}

export type AopSubmissionState = 'submitted' | 'withdrawn' | 'returned' | 'approved';

export interface AopIterationStatus {
  current: {
    id: string;
    name: string;
    openedBy: string;
    openedAt: string;
  } | null;
  /** Keyed by department tab label; a department with no entry has not been submitted. */
  departments: Record<
    string,
    {
      state: AopSubmissionState;
      submittedBy: string | null;
      submittedAt: string | null;
      /** Anna or David submitted it in the head's place. */
      submittedOnBehalf?: boolean;
      /** The submission's revision — changes with a review edit, which keeps `submittedAt`. */
      lastChangeAt?: string | null;
      withdrawnBy: string | null;
      /** 'edited' — a draft changed after submitting; 'head' — the head withdrew it;
       * 'on_behalf' — Anna or David withdrew it in the head's place. */
      withdrawReason: string | null;
      drafts: number;
      /** Submitted or approved, but the live drafts no longer match the snapshot (a missed
       * withdrawal, an edit straight in Airtable, or the approval race). Anna cannot approve a
       * stale submission; she can reopen a stale approved one. */
      stale?: boolean;
      /** Ticks so far, keyed by reviewer; `by` is who clicked (Anna/David when on behalf). */
      reviews?: Record<string, { at: string | null; by: string | null; onBehalf?: boolean }>;
      returnedBy?: string | null;
      /** Only for the department's head, the reviewers and Anna/David; null for everyone else. */
      returnNote?: string | null;
      /** Roles declined one by one out of this submission; `note` like `returnNote` (reopen's note). */
      declined?: { recordId: string; title: string | null; by: string | null; at: string | null; note: string | null }[];
      approvedBy?: string | null;
      approvedAt?: string | null;
      /** The last "Sync to hiring plan" of this approval, with each row's outcome. */
      sync?: AopSyncResult | null;
    }
  >;
  /** Anna and David open iterations. */
  canOpen: boolean;
  /** Anna and David approve, reopen, and tick for an absent reviewer. */
  canApprove?: boolean;
  /** This caller's reviewer key (karen, maxi, florian), or null. */
  reviewerKey?: string | null;
  /** The reviewers, in order. */
  reviewers?: { key: string; name: string }[];
  /** The departments this caller heads, i.e. may submit. */
  headOf: string[];
  /** Anna and David may submit and withdraw any department in its head's place. */
  canActForHeads?: boolean;
  /** Per department label: roles set aside to revisit, with the reviewer's note (null when this
   * viewer may not read it) — set aside now (`back: false`) or back in the plan (`back: true`). */
  revisit?: Record<
    string,
    { recordId: string; title: string | null; by: string | null; at: string | null; note: string | null; iterationName: string | null; back: boolean }[]
  >;
}

export interface AopTimeline extends HiringCapacityTimeline {
  dataQuality: HiringCapacityTimeline['dataQuality'] & {
    /** The AOP table could not be read, so the drafts are missing from the page. */
    aopDraftsUnavailable?: string | null;
    /** The CAC roles could not be read, so hiring-plan roles and proposals booked as CAC may be counted. */
    cacUnavailable?: string | null;
    /** The targets could not be read, or some months were entered twice. */
    targetsUnavailable?: string | null;
    /** The iteration status could not be read. */
    iterationUnavailable?: string | null;
  };
  /** The open iteration and each department's submission (`proxy-server/lib/aopIterations.js`). */
  iteration?: AopIterationStatus | null;
  /** Keyed by department tab label — only the departments this viewer may see figures for. */
  targets?: Record<string, AopDepartmentTargets>;
  /** Every department added up — only for a viewer who sees all of them, else null or absent. */
  company?: AopCompanyTargets | null;
}

/** The org tree with the AOP drafts on it, plus the caller's viewer scope. A 403 is its own error. */
export async function fetchAopTimeline(): Promise<AopTimeline> {
  const res = await authenticatedFetch(`${AOP_URL}/timeline`, { suppressErrorStatuses: [403] });
  if (res.status === 403) throw new InternalPlaygroundAccessError('You do not have access to AOP planning.');
  if (!res.ok) throw new Error(`Failed to load the AOP planning view (${res.status})`);
  return res.json();
}

/** One draft on the Approvals tab (`proxy-server/lib/aopApprovals.js`). */
export interface AopApprovalDraft {
  recordId: string;
  title: string;
  role: string | null;
  trackAndLevel: string | null;
  plannedStartDate: string | null;
  hiringManager: string | null;
  unit: string | null;
  reasonForHiring: string | null;
  priority: string | null;
  /** "Why this role?", written out. */
  rationale: string | null;
  /** OTE, else the band average, per year — null with `costNote` when there is none or it is hidden. */
  yearlyCost: number | null;
  costNote: string | null;
}

/** A role declined in a review and kept for later iterations (`proxy-server/lib/aopDeclined.js`). */
export interface AopDeclinedRole {
  recordId: string;
  title: string;
  role: string | null;
  trackAndLevel: string | null;
  plannedStartDate: string | null;
  hiringManager: string | null;
  rationale: string | null;
  declinedBy: string | null;
  declinedAt: string | null;
  note: string | null;
  iterationName: string | null;
}

export interface AopApprovals {
  /** `onPage: false` — a department no longer on the page, or the declined roles with no review on
   * record: only their declined roles are shown (and restorable), never a state or counts. */
  departments: { department: string; onPage?: boolean; drafts: AopApprovalDraft[]; declined?: AopDeclinedRole[] }[];
  /** The declined roles could not be read; the plan under review is still complete. */
  declinedUnavailable?: boolean;
  generatedAt: string;
}

/** A CHILD of the timeline's key: every action that refreshes the page (a tick, an approval, a
 * submit, a draft edit) invalidates that key, and so this one too — the drafts a reviewer reads
 * never lag behind the state they act on. */
export const AOP_APPROVALS_QUERY_KEY = [...AOP_TIMELINE_QUERY_KEY, 'approvals'];

/** Every department's drafts with their rationale — the reviewers and Anna/David only (else 403). */
export async function fetchAopApprovals(): Promise<AopApprovals> {
  const res = await authenticatedFetch(`${AOP_URL}/approvals`, { suppressErrorStatuses: [403, 502, 503] });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Failed to load the approvals (${res.status})`);
  return body;
}

/** A department locked by an approval (409) or a lock that could not be checked (503): the
 * proxy's own words, so the form can show them. */
async function throwIterationRefusal(res: Response): Promise<void> {
  if (res.status !== 409 && res.status !== 503) return;
  const body = await res.json().catch(() => ({}));
  throw new Error(body.error || `The change was refused (${res.status}).`);
}

/** File one 2027 hire as an AOP draft. There is no status field: the proxy always files `AOP [proposed]`. */
/** Departments whose submission this change withdrew (`withdrawnFrom` on every draft change). */
const withdrawnFromOf = (body: { withdrawnFrom?: unknown }): string[] =>
  Array.isArray(body?.withdrawnFrom) ? (body.withdrawnFrom as string[]) : [];

/** File one 2027 hire as an AOP draft; the proxy always files it as `AOP [proposed]`. */
export async function createAopDraft(
  input: ProposedRoleInput
): Promise<{ id: string; priorityDropped: boolean; withdrawnFrom: string[] }> {
  const res = await authenticatedFetch(`${AOP_URL}/drafts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    suppressErrorStatuses: [400, 409, 503],
  });
  await throwIterationRefusal(res);
  if (res.status === 400) {
    const body = await res.json().catch(() => ({}));
    throw new ProposalValidationError(body.error || 'The role is incomplete.', body.details || {});
  }
  if (!res.ok) throw new Error(`Failed to save the role (${res.status})`);
  const body = await res.json();
  return { id: body.id, priorityDropped: Boolean(body.priorityDropped), withdrawnFrom: withdrawnFromOf(body) };
}

/** File several drafts that share everything but track and start date. */
export async function createAopDrafts(
  input: BulkProposalInput
): Promise<{ ids: string[]; priorityDropped: boolean; withdrawnFrom: string[] }> {
  const res = await authenticatedFetch(`${AOP_URL}/drafts/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    suppressErrorStatuses: [400, 409, 503],
  });
  await throwIterationRefusal(res);
  if (res.status === 400) {
    const body = await res.json().catch(() => ({}));
    throw new BulkProposalValidationError(body.error || 'The roles are incomplete.', {
      shared: body.details?.shared || {},
      rows: body.details?.rows || {},
    });
  }
  // 207 is a 2xx: checked before `res.ok`, because some rows DID land.
  if (res.status === 207) {
    const body = await res.json().catch(() => ({}));
    throw new BulkPartialFailureError(
      body.error || 'Airtable accepted some of the roles and refused the rest.',
      body.ids || [],
      body.confirmed !== false
    );
  }
  if (!res.ok) throw new Error(`Failed to save the roles (${res.status})`);
  const body = await res.json();
  return { ids: body.ids || [], priorityDropped: Boolean(body.priorityDropped), withdrawnFrom: withdrawnFromOf(body) };
}

export interface AopDraft {
  source: typeof AOP_SOURCE;
  id: string;
  status: string;
  values: RoleEditValues;
  plannedStartDate: string | null;
  hiringManagerName: string | null;
  compensation: RoleCompensation | null;
}

const draftUrl = (id: string) => `${AOP_URL}/drafts/${encodeURIComponent(id)}`;

export async function fetchAopDraft(id: string): Promise<{ role: AopDraft }> {
  const res = await authenticatedFetch(draftUrl(id), { suppressErrorStatuses: [403, 404, 409] });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Could not load the role (${res.status})`);
  return body;
}

/** Every change is saved straight away — a draft has no approvals, so there is no 10% rule. */
export async function saveAopDraft(
  id: string,
  changes: Partial<RoleEditValues>,
  { inReview = false }: { inReview?: boolean } = {}
): Promise<{ changed: boolean; withdrawnFrom: string[] }> {
  const res = await authenticatedFetch(draftUrl(id), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    // `inReview` (Approvals tab): a reviewer's edit keeps a submitted department submitted.
    body: JSON.stringify({ changes, ...(inReview ? { inReview: true } : {}) }),
    suppressErrorStatuses: [400, 403, 404, 409, 503],
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 400 && body.details) {
    throw new RoleEditValidationError(body.error || 'The edit is invalid.', body.details);
  }
  if (!res.ok) throw new Error(body.error || `Could not save the change (${res.status})`);
  return { changed: Boolean(body.changed), withdrawnFrom: withdrawnFromOf(body) };
}

/** Take a draft back out of the plan. The proxy keeps the row as `AOP [cancelled]`. */
export async function removeAopDraft(id: string): Promise<{ withdrawnFrom: string[] }> {
  const res = await authenticatedFetch(`${draftUrl(id)}/remove`, {
    method: 'POST',
    suppressErrorStatuses: [400, 403, 404, 409, 503],
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Could not remove the role (${res.status})`);
  return { withdrawnFrom: withdrawnFromOf(body) };
}

/** POST to an iteration route; the proxy's message on any refusal. */
async function iterationPost(path: string, body?: object): Promise<void> {
  const res = await authenticatedFetch(`${AOP_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
    // 502 too: the routes answer it with a message this page shows inline.
    suppressErrorStatuses: [400, 403, 404, 409, 502, 503],
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    // The unassigned-drafts refusal names the drafts — the head needs to know which ones.
    const titles: string[] = Array.isArray(data.details?.titles) ? data.details.titles : [];
    const message = data.error || `The request was refused (${res.status}).`;
    throw new Error(titles.length ? `${message} (${titles.join(', ')})` : message);
  }
}

/** Open the next, named iteration — Anna (or David) only; the proxy decides. */
export async function openAopIteration(name: string): Promise<{ returned: number; notReturned: { recordId: string; title: string }[] | null }> {
  const res = await authenticatedFetch(`${AOP_URL}/iterations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
    suppressErrorStatuses: [400, 403, 409, 502],
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `The request was refused (${res.status}).`);
  // How many roles set aside earlier came back for revisiting, and any that did not (null: unknown).
  return { returned: Number(body.returned) || 0, notReturned: Array.isArray(body.notReturned) ? body.notReturned : body.notReturned === null ? null : [] };
}

/** The department head (or Anna/David in their place) submits it for the open iteration. */
export const submitAopDepartment = (department: string) =>
  iterationPost(`/iterations/current/departments/${encodeURIComponent(department)}/submit`);

/** The department head (or Anna/David in their place) withdraws a submitted department. */
export const withdrawAopDepartment = (department: string) =>
  iterationPost(`/iterations/current/departments/${encodeURIComponent(department)}/withdraw`);

/** Tick "reviewed" — for yourself, or (Anna/David) for an absent reviewer. */
export const reviewAopDepartment = (department: string, onBehalfOf?: string) =>
  iterationPost(`/iterations/current/departments/${encodeURIComponent(department)}/review`, onBehalfOf ? { onBehalfOf } : {});

/** Restore a declined role into its department's plan (Anna/David) — a draft change like any other. */
export const restoreAopDraft = (recordId: string) => iterationPost(`/drafts/${encodeURIComponent(recordId)}/restore`);

/** Decline ONE role of a submitted department, with a note — it leaves the plan, the rest stays submitted. */
export const declineAopDraft = (department: string, recordId: string, note: string) =>
  iterationPost(
    `/iterations/current/departments/${encodeURIComponent(department)}/drafts/${encodeURIComponent(recordId)}/decline`,
    { note }
  );

/** Approve a department (Anna, David backup) — it locks. `tickMissing` ticks for every reviewer
 * still missing first, on the caller's behalf — only while the submission (`submittedAt`) and the
 * missing reviewers (`tickFor`) are still the ones the confirmation showed, else the proxy refuses. */
export const approveAopDepartment = (
  department: string,
  seen?: { tickMissing: true; submittedAt: string | null; lastChangeAt: string | null; tickFor: string[] }
) => iterationPost(`/iterations/current/departments/${encodeURIComponent(department)}/approve`, seen);

/** Reopen an approved department (Anna, David backup), with a note — it unlocks. */
export const reopenAopDepartment = (department: string, note: string) =>
  iterationPost(`/iterations/current/departments/${encodeURIComponent(department)}/reopen`, { note });

/** Write an approved department into hiring_plan as "Planned in AOP" (Anna, David backup). */
export async function syncAopDepartment(department: string): Promise<AopSyncResult> {
  const res = await authenticatedFetch(`${AOP_URL}/iterations/current/departments/${encodeURIComponent(department)}/sync`, {
    method: 'POST',
    suppressErrorStatuses: [400, 403, 404, 409, 502, 503],
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `The sync was refused (${res.status}).`);
  // A 2xx without a result must not read as "synced": the row would say "Not synced yet".
  if (!body.sync) throw new Error('The sync answered without a result. Reload the page to see what was written.');
  return body.sync;
}
